/**
 * Store pour la gestion des permissions et notamment des groupes de permissions
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

export const usePermissionStore = defineStore('permission', (() => {

    // State de la liste des sections
    const sectionsList = ref({});

    // State pour différencier les données de la liste des sections
    const sections = ref([]);

    /**
     * Retourne la liste des sections
     * @return array
     */
    const getSections = computed(() => sectionsList.value);

    /**
     * Retourne une section par son identifiant
     * @param int sectionId
     * @return object|null
     */
    const getSectionById = computed(() => {
        return ((sectionId) => {
            const index = sections.value.findIndex((section) => section.id === sectionId);
            return (index !== -1 ? sections.value[index] : null);
        });
    });

    /**
     * Chargement des sections
     * @param object params
     * @return Promise
     */
    const loadSections = ((params) => {

        const path = '/api/admin/permissions/sections';

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetSectionList();
                }

                updateSectionList(response.data.data);

                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Chargement de la section dont l'identifiant est en paramètre
     * @param int sectionId
     * @return Promise
     */
    const loadSectionById = ((sectionId) => {
        const section = getSectionById.value(sectionId);
        if(section !== null) {
            return Promise.resolve(section);
        }

        const path = '/api/admin/permissions/sections/:sectionId'.replace(':sectionId', sectionId);

        return axios.get(path).then((response) => {
            if (response && response.data && response.data.success && response.data.section) {
                updateSection(response.data.section);
            }
        });
    });

    /**
     * Mise à jour de la section dans la liste
     * @param object section
     * @return void
     */
    const updateSection = ((section) => {
        const currentSections = sections.value;

        const index = currentSections.findIndex((sectionFromState) => sectionFromState.id === section.id);
        if(index !== -1) {
            sections.value[index] = section;
        } else {
            sections.value.push(section);
        }
    });

    /**
     * Mise à jour du groupe dans la section
     * @param int sectionId Identifiant de la section auquel appartient le groupe
     * @param object group Nouvelles données du groupe
     * @return void
     */
    const updateGroup = ((sectionId, group) => {
        const sectionIndex = sections.value.findIndex((sectionFromState) => sectionFromState.id === sectionId);
        if(sectionIndex === -1) {
            return;
        }

        const section = sections.value[sectionIndex];
        const groups = section.groups || [];
        const groupIndex = groups.findIndex((groupFromState) => groupFromState.id === group.id);

        if(groupIndex === -1) {
            groups.push(group);
        } else {
            groups[groupIndex] = group;
        }

        section.groups = groups;

        updateSection(section);
    });

    /**
     * Mise à jour de la permission dans le groupe
     * @param int sectionId Identifiant de la section
     * @param int groupId Identifiant du groupe
     * @param object permission Nouvelles données de la permission
     * @return void
     */
    const updatePermission = ((sectionId, groupId, permission) => {

        const sectionIndex = sections.value.findIndex((sectionFromState) => sectionFromState.id === sectionId);
        if(sectionIndex === -1) {
            return;
        }

        const section = sections.value[sectionIndex];
        const groups = section.groups || [];
        const groupIndex = groups.findIndex((groupFromState) => groupFromState.id === groupId);
        if(groupIndex === -1) {
            return;
        }

        const permissions = groups[groupIndex].permissions || [];
        const permissionIndex = permissions.findIndex((permissionFromState) => permissionFromState.id === permission.id);

        if(permissionIndex === -1) {
            permissions.push(permission);
        } else {
            permissions[permissionIndex] = permission;
        }

        section.groups[groupIndex].permissions = permissions;

        updateSection(section);

    });

    /**
     * Mise à jour de la liste des sections
     * @param object data
     * @return void
     */
    const updateSectionList = ((data) => {

        if(! sectionsList.value.total) {
            sectionsList.value = {
                total: data.total,
                sections: data.items
            };
            data.items.forEach((section) => {
                updateSection(section);
            });
        } else {
            sectionsList.value.total = data.total;

            const currentSections = sectionsList.value.sections;
            const newSections = data.items.filter((item) => ! currentSections.find((current) => current.id === item.id));
            sectionsList.value.sections = [ ...currentSections, ...newSections ];

            newSections.forEach((section) => {
                updateSection(section);
            });
        }

    });

    /**
     * Réinitialisation de la liste des sections
     * @return void
     */
    const resetSectionList = (() => {
        sectionsList.value = {};
    });

    /**
     * Ajoute une section
     * @param object section Données de la section à ajouter
     * @return void
     */
    const addSection = ((section) => {

        if(sectionsList.value.total === undefined) {
            sectionsList.value = {
                total: 0,
                sections: []
            };
        }

        sectionsList.value.total += 1;
        sectionsList.value.sections.push(section);

        sections.value.push(section);
    });

    return {
        getSections,
        getSectionById,
        loadSections,
        loadSectionById,
        addSection,
        updateGroup,
        updatePermission
    };

}));
